import {
  BUTTON_TYPE,
  INTEGRATION_BUTTONS_DEFAULT_SCHEMA,
  SYSTEM_BUTTONS_DEFAULT_SCHEMA
} from '../components/engine/FormBuilderCustomButtonsEngine';
import SerializationCopy from './SerializationCopy';
import generateId from './generateId';
import { cloneDeep } from 'lodash';
import fetchAPI from './fetchAPI';

/**
 *
 * @param {Object} formButtons  - stracture should be based on values under "buttons" kept in the form schema
 * @param {String} currentStatus - current status of the submission
 * @param {Boolean} isAdminForm - is admin form or end user form
 * @param {Array} buttonsId2Filter - filter specific buttons
 * @returns buttons to show in the same structure as formButtons
 */
export const getButtons2Show = (
  formButtons,
  currentStatus,
  isAdminForm,
  buttonsId2Filter = []
) => {
  if (!formButtons) {
    const updatedButtons2Filter = buttonsId2Filter;
    const currentIntegrationButtons = Object.values(
      INTEGRATION_BUTTONS_DEFAULT_SCHEMA
    ).filter(ib => !buttonsId2Filter.includes(ib.id));
    const showingThirdfortButton = currentIntegrationButtons.find(
      one =>
        one.id === INTEGRATION_BUTTONS_DEFAULT_SCHEMA.SUBMIT_AND_THIRDFORT.id
    );
    if (showingThirdfortButton) {
      updatedButtons2Filter.push(SYSTEM_BUTTONS_DEFAULT_SCHEMA.SUBMIT.id);
    }
    return {
      systemButtons: Object.values(SYSTEM_BUTTONS_DEFAULT_SCHEMA).filter(
        sb => !buttonsId2Filter.includes(sb.id)
      ),
      customButtons: [],
      integrationButtons: currentIntegrationButtons
    };
  }

  const systemButtons = formButtons.systemButtons || [];
  const customButtons = formButtons.customButtons || [];
  const integrationButtons = formButtons.integrationButtons || [];

  const customButtonFilter = b => {
    if (buttonsId2Filter.includes(b.id)) {
      return false;
    }
    if (b.type !== 'both' && isAdminForm !== (b.type === BUTTON_TYPE.ADMIN)) {
      // button shouldn't display for the current form setup (admin btn in eu form or the other way around)
      return false;
    }
    if (b.statuses.includes('all')) {
      // button should display for all statuses
      return true;
    }
    if (b.statuses.includes(currentStatus)) {
      // button should display for the current status
      return true;
    }

    if (b.statuses.includes('new') && !currentStatus) {
      // special treatment for "no status" - new submission
      return true;
    }

    return false;
  };

  const currentStatusCB = customButtons.filter(customButtonFilter);

  const currentStatusIB = integrationButtons.filter(customButtonFilter);

  const currentStatusSB = systemButtons.filter(sb => {
    if (buttonsId2Filter.includes(sb.id)) {
      return false;
    }
    const hasThirdFortButton = currentStatusIB.find(
      one =>
        one.id === INTEGRATION_BUTTONS_DEFAULT_SCHEMA.SUBMIT_AND_THIRDFORT.id
    );
    // now we can filter based on integration buttons
    if (
      isAdminForm &&
      !currentStatus &&
      !hasThirdFortButton &&
      (sb.id === SYSTEM_BUTTONS_DEFAULT_SCHEMA.SUBMIT.id ||
        sb.id === SYSTEM_BUTTONS_DEFAULT_SCHEMA.SIGN_AND_SUBMIT.id)
    ) {
      // kinda bad filter but is used for very specific location...
      // it's used for the admin drawer, when creating a new submission, we want to show submit/save and submit button
      // sorry :/
      return true;
    }

    // no standard submit buttons allowed when there's a thirdfort submit button
    if (
      hasThirdFortButton &&
      (sb.id === SYSTEM_BUTTONS_DEFAULT_SCHEMA.SUBMIT.id ||
        sb.id === SYSTEM_BUTTONS_DEFAULT_SCHEMA.SIGN_AND_SUBMIT.id)
    ) {
      return false;
    }

    if (
      sb.id === SYSTEM_BUTTONS_DEFAULT_SCHEMA.REQUEST_MORE_INFO.id &&
      !currentStatus
    ) {
      // explicity ignore request more info from new submissions
      return false;
    }

    if (customButtonFilter(sb)) {
      // passed custom button filter function
      return true;
    }
    if (
      !currentStatusCB.length &&
      sb.id !== SYSTEM_BUTTONS_DEFAULT_SCHEMA.REQUEST_MORE_INFO.id
    ) {
      // no custom buttons for this status - show system button (except for request more info which
      // is a special case - we don't force it)
      return true;
    }
    return false;
  });

  return {
    systemButtons: currentStatusSB,
    customButtons: currentStatusCB,
    integrationButtons: currentStatusIB
  };
};

// ==========================================================================================================

/**
 *
 * Map the flat list of fields to sections and their fields
 *
 * @param {Array} fields - all form fields
 * @returns Object mapped as key - {String} section id | value - {Array} - list of fields under that section
 */
export const mapSectionsToFields = fields => {
  let currentSection, result, sortedFields;
  result = {};
  sortedFields = [...fields].sort((a, b) => {
    if (a.position < b.position) {
      return -1;
    }
    if (a.position > b.position) {
      return 1;
    }
    return 0;
  });
  currentSection = null;

  sortedFields.forEach(one => {
    if (one.uiType === 'section_break') {
      currentSection = one.internalId;
      result[currentSection] = [];
    } else {
      if (currentSection) {
        result[currentSection].push(one.internalId);
      }
    }
  });
  return result;
};

/**
 *
 * Given a form object, return an object which maps sections to all of their fields (end user and admin)
 *
 * @param {Object} form - the full form object
 * @returns Object mapped as key - {String} section id | value - {Array} - list of fields under that section
 */
export const getAllFieldsBySection = (fields, adminFields) => {
  const fieldsCopy = SerializationCopy(fields);
  const adminFieldsCopy = SerializationCopy(adminFields);
  fieldsCopy.forEach(one => {
    one.adminField = false;
  });
  adminFieldsCopy.forEach(one => {
    one.adminField = true;
  });
  const allFields = [...fieldsCopy, ...adminFieldsCopy];
  allFields.sort((a, b) => a.position - b.position);
  const allFieldsBySection = {};
  let currentSectionId = 'unnamed-starting-section';
  let allFieldsOfCurrentSection = [];
  allFields.forEach(one => {
    if (one.uiType === 'section_break') {
      allFieldsBySection[currentSectionId] = allFieldsOfCurrentSection;
      allFieldsOfCurrentSection = [];
      currentSectionId = one.internalId;
    }
    allFieldsOfCurrentSection.push(one);
  });
  // handle the final section
  if (currentSectionId && !allFieldsBySection[currentSectionId]) {
    allFieldsBySection[currentSectionId] = allFieldsOfCurrentSection;
  }
  return allFieldsBySection;
};

export const getAllIdsOfSection = sectionFields => {
  return sectionFields.map(one => one.internalId);
};

const generateFieldId = () => 'f' + generateId();

export const mapExistingFieldIdsToNewIds = oldIds => {
  let result = {};
  oldIds.forEach(one => {
    result[one] = generateFieldId();
  });
  return result;
};

export const doesUITypeHaveSubfields = uiType => {
  return [
    'credas',
    'credas_out_of_scope',
    'currency_conversion',
    'lookup',
    'user_lookup',
    'thirdfort',
    'worldcheck',
    'worldcheck_org'
  ].includes(uiType);
};

export const getDuplicateSection = (sectionToDuplicate, idMapOldToNew) => {
  const newSection = [];
  sectionToDuplicate.forEach((oneOriginalField, i) => {
    const copyOfField = cloneDeep(oneOriginalField);
    const newFieldId = idMapOldToNew[copyOfField.internalId];
    let oldParentFieldId;
    copyOfField.internalId = newFieldId;
    if (
      copyOfField.lookupInfo &&
      copyOfField.lookupInfo.subFieldInfo &&
      copyOfField.lookupInfo.subFieldInfo.parentFieldId
    ) {
      oldParentFieldId = copyOfField.lookupInfo.subFieldInfo.parentFieldId;
      if (idMapOldToNew[oldParentFieldId]) {
        copyOfField.lookupInfo.subFieldInfo.parentFieldId =
          idMapOldToNew[oldParentFieldId];
      }
    }
    newSection.push(copyOfField);
  });
  return newSection;
};

export const getDuplicateRules = (existingRules, idMapOldToNew) => {
  const rulesToAdd = [];
  const oldFields = Object.keys(idMapOldToNew);
  existingRules.forEach(oneRule => {
    if (oldFields.includes(oneRule.fieldId)) {
      const newRule = cloneDeep(oneRule);
      let newCondition = oneRule.condition || '';
      if (idMapOldToNew[oneRule.fieldId]) {
        newRule.fieldId = idMapOldToNew[oneRule.fieldId];
      }
      if (oneRule.condition) {
        oldFields.forEach(oneId => {
          newCondition = newCondition.replaceAll(oneId, idMapOldToNew[oneId]);
        });
        newRule.condition = newCondition;
      }
      delete newRule.id;
      delete newRule.metadata;
      rulesToAdd.push(newRule);
    }
  });
  return rulesToAdd;
};

export const getStatusesForFormListPromise = listOfFormIds => {
  let subQueries, query;
  subQueries = [];
  listOfFormIds.forEach(one => {
    subQueries.push(`{"field":"id", "op": "=", "value": "${one}"}`);
  });
  query = '[[' + subQueries.join(',') + ']]';
  return fetchAPI(`/forms/v1/forms?compact=true&query="${query}"`).then(res => {
    let result = {};
    if (res && res.results) {
      res.results.forEach(oneRes => {
        result[oneRes.id] = oneRes.customStatuses;
      });
    }
    return result;
  });
};

export const isViewOnlyAdminOfForm = (formId, decoded) => {
  let i, j, queryFilters, roles, oneQueryFilter, oneRole;
  if (decoded && decoded.privileges) {
    if (decoded.roles) {
      roles = decoded.roles;
      for (i = 0; i < roles.length; i++) {
        oneRole = roles[i];
        if (oneRole.role === 'form_admin') {
          queryFilters = oneRole.queryFilters;
          if (!queryFilters.length) {
            return false;
          }
          for (j = 0; j < queryFilters.length; j++) {
            oneQueryFilter = queryFilters[j];
            if (oneQueryFilter.assetId === formId) {
              if (oneQueryFilter.viewOnly) {
                return true;
              }
            }
          }
        }
      }
    }
  }
  return false;
};
